// /home/labdoo/zitech-nexus/frontend/src/pages/admin/AdminApplicationsList.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jobsAPI } from '../../services/api';
import './AdminApplicationsList.css';

interface Application {
  id: number;
  job_title: string;
  job?: number;
  full_name: string;
  email: string;
  phone: string;
  status: string;
  applied_at: string;
  account_created?: boolean;
  user_email?: string;
}

// SVG Icons
const Icons = {
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="18" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <path d="M8 7H16" />
      <path d="M8 11H14" />
      <path d="M8 15H12" />
    </svg>
  ),
  View: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  NewAccount: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
      <path d="M19 8L21 10L25 6" strokeWidth="3" />
    </svg>
  ),
  Email: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Phone: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="aal-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Clear: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Export: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  Download: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 17L12 21L20 17" />
      <path d="M12 3V15M12 15L8 11M12 15L16 11" />
    </svg>
  ),
  ChevronDown: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Filter: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3" />
    </svg>
  ),
};

const AdminApplicationsList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<Application[]>([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check URL params for jobId filter
    const params = new URLSearchParams(location.search);
    const jobId = params.get('jobId');
    if (jobId) {
      setSelectedJobId(parseInt(jobId));
    }
    fetchApplications();
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      
      // If jobId is selected, fetch applications for that job only
      let response;
      if (selectedJobId) {
        response = await jobsAPI.getJobApplications(selectedJobId);
      } else {
        response = await jobsAPI.getAllApplications();
      }
      
      setApplications(response.data);
      
      // If jobId is selected, set the job filter
      if (selectedJobId && response.data.length > 0) {
        const jobTitle = response.data[0]?.job_title;
        if (jobTitle) {
          setJobFilter(jobTitle);
        }
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'pending': return '#f59e0b';
      case 'reviewed': return '#0ea5e9';
      case 'shortlisted': return '#8b5cf6';
      case 'interviewed': return '#06b6d4';
      case 'accepted': return '#10b981';
      case 'rejected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'pending': return 'Pending';
      case 'reviewed': return 'Reviewed';
      case 'shortlisted': return 'Shortlisted';
      case 'interviewed': return 'Interviewed';
      case 'accepted': return 'Accepted';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  const getStatusBackground = (status: string) => {
    switch(status) {
      case 'pending': return '#fef3c7';
      case 'reviewed': return '#dbeafe';
      case 'shortlisted': return '#ede9fe';
      case 'interviewed': return '#cffafe';
      case 'accepted': return '#d1fae5';
      case 'rejected': return '#fee2e2';
      default: return '#f3f4f6';
    }
  };

  // Get unique job titles for filter
  const uniqueJobs = useMemo(() => {
    const jobs = new Set(applications.map(app => app.job_title));
    return Array.from(jobs);
  }, [applications]);

  // Filter applications
  const filteredApplications = useMemo(() => {
    let filtered = [...applications];

    if (filter !== 'all') {
      filtered = filtered.filter(app => app.status === filter);
    }

    if (jobFilter !== 'all') {
      filtered = filtered.filter(app => app.job_title === jobFilter);
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(app =>
        app.full_name.toLowerCase().includes(search) ||
        app.email.toLowerCase().includes(search) ||
        app.job_title.toLowerCase().includes(search) ||
        app.phone.includes(search) ||
        app.id.toString().includes(search)
      );
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(app => {
        const appDate = new Date(app.applied_at);
        const appDateOnly = new Date(appDate.getFullYear(), appDate.getMonth(), appDate.getDate());
        
        switch(dateFilter) {
          case 'today':
            return appDateOnly.getTime() === today.getTime();
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return appDateOnly >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return appDateOnly >= monthAgo;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [applications, filter, searchTerm, jobFilter, dateFilter]);

  const statusCounts = {
    all: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    shortlisted: applications.filter(a => a.status === 'shortlisted').length,
    interviewed: applications.filter(a => a.status === 'interviewed').length,
    accepted: applications.filter(a => a.status === 'accepted').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const clearAllFilters = () => {
    setFilter('all');
    setSearchTerm('');
    setJobFilter('all');
    setDateFilter('all');
    setSelectedJobId(null);
    // Navigate to clear the jobId param
    navigate('/dashboard/admin/applications');
    fetchApplications();
  };

  // Remove job filter and show all applications
  const clearJobFilter = () => {
    setSelectedJobId(null);
    setJobFilter('all');
    navigate('/dashboard/admin/applications');
    fetchApplications();
  };

  const toggleExportDropdown = () => {
    setShowExportDropdown(!showExportDropdown);
  };

  // Export to CSV
  const exportToCSV = async () => {
    if (filteredApplications.length === 0) {
      alert('No applications to export');
      return;
    }

    setExporting(true);
    setShowExportDropdown(false);
    
    try {
      const headers = [
        'Application ID',
        'Job Title',
        'Applicant Name',
        'Email',
        'Phone',
        'Status',
        'Applied Date',
        'Account Created'
      ];

      const rows = filteredApplications.map(app => [
        app.id,
        app.job_title,
        app.full_name,
        app.email,
        app.phone,
        getStatusLabel(app.status),
        new Date(app.applied_at).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        }),
        app.account_created ? 'Yes' : 'No'
      ]);

      let csvContent = headers.join(',') + '\n';
      
      rows.forEach(row => {
        const escapedRow = row.map(field => {
          const str = String(field);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        });
        csvContent += escapedRow.join(',') + '\n';
      });

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      const date = new Date().toISOString().split('T')[0];
      const filename = `applications_export_${date}.csv`;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      alert(`✅ Successfully exported ${filteredApplications.length} applications!`);
    } catch (error) {
      console.error('Error exporting applications:', error);
      alert('Failed to export applications. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Export to JSON
  const exportToJSON = async () => {
    if (filteredApplications.length === 0) {
      alert('No applications to export');
      return;
    }

    setExporting(true);
    setShowExportDropdown(false);
    
    try {
      const data = filteredApplications.map(app => ({
        id: app.id,
        job_title: app.job_title,
        applicant_name: app.full_name,
        email: app.email,
        phone: app.phone,
        status: getStatusLabel(app.status),
        applied_at: new Date(app.applied_at).toISOString(),
        account_created: app.account_created
      }));

      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      const date = new Date().toISOString().split('T')[0];
      const filename = `applications_export_${date}.json`;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      alert(`✅ Successfully exported ${filteredApplications.length} applications!`);
    } catch (error) {
      console.error('Error exporting applications:', error);
      alert('Failed to export applications. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Export to PDF (Print)
  const exportToPDF = () => {
    if (filteredApplications.length === 0) {
      alert('No applications to export');
      return;
    }

    setShowExportDropdown(false);
    
    // Store current body content
    const originalContent = document.body.innerHTML;
    
    // Create print wrapper with only the table
    const tableHTML = document.querySelector('.aal-table-wrapper')?.outerHTML || '';
    
    const printContent = `
      <div class="aal-print-container">
        <div class="aal-print-header">
          <h1>Job Applications Report</h1>
          <p>Generated: ${new Date().toLocaleString()}</p>
          <p>Total Applications: ${filteredApplications.length}</p>
          ${selectedJobId ? `<p>Job Filter: ${applications[0]?.job_title || 'Selected Job'}</p>` : ''}
        </div>
        ${tableHTML}
        <div class="aal-print-footer">
          <p>Generated by ZiTech Solutions - ${new Date().toLocaleString()}</p>
        </div>
      </div>
    `;
    
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  // Show job filter indicator
  const showJobFilter = selectedJobId || jobFilter !== 'all';

  if (loading) {
    return (
      <div className="aal-loading-container">
        <div className="aal-spinner">
          <Icons.Spinner />
        </div>
        <p className="aal-loading-text">Loading applications...</p>
      </div>
    );
  }

  return (
    <div className="aal-container">
      {/* Header */}
      <div className="aal-header">
        <div className="aal-header-left">
          <h1 className="aal-title">
            Job Applications
            {selectedJobId && applications.length > 0 && (
              <span className="aal-job-filter-badge">
                <Icons.Filter />
                {applications[0]?.job_title || 'Selected Job'}
              </span>
            )}
          </h1>
          <p className="aal-subtitle">
            {selectedJobId 
              ? `Showing applications for: ${applications[0]?.job_title || 'Selected Job'}`
              : 'Review and manage all job applications'}
          </p>
        </div>
        <div className="aal-header-stats">
          <div className="aal-export-wrapper" ref={dropdownRef}>
            <button 
              className="aal-export-btn"
              onClick={toggleExportDropdown}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <span className="aal-spinner-small"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <Icons.Export />
                  Export
                  <Icons.ChevronDown />
                </>
              )}
            </button>
            {showExportDropdown && (
              <div className="aal-export-dropdown">
                <button onClick={exportToCSV} className="aal-export-option" disabled={exporting}>
                  <Icons.Download />
                  Export as CSV
                </button>
                <button onClick={exportToJSON} className="aal-export-option" disabled={exporting}>
                  <Icons.Download />
                  Export as JSON
                </button>
                <button onClick={exportToPDF} className="aal-export-option" disabled={exporting}>
                  <Icons.Download />
                  Export as PDF
                </button>
              </div>
            )}
          </div>
          <span className="aal-total-badge">
            Total: {applications.length}
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="aal-stats-grid">
        <div className="aal-stat-card aal-stat-pending">
          <span className="aal-stat-label">Pending</span>
          <span className="aal-stat-number">{statusCounts.pending}</span>
        </div>
        <div className="aal-stat-card aal-stat-reviewed">
          <span className="aal-stat-label">Reviewed</span>
          <span className="aal-stat-number">{statusCounts.reviewed}</span>
        </div>
        <div className="aal-stat-card aal-stat-shortlisted">
          <span className="aal-stat-label">Shortlisted</span>
          <span className="aal-stat-number">{statusCounts.shortlisted}</span>
        </div>
        <div className="aal-stat-card aal-stat-interviewed">
          <span className="aal-stat-label">Interviewed</span>
          <span className="aal-stat-number">{statusCounts.interviewed}</span>
        </div>
        <div className="aal-stat-card aal-stat-accepted">
          <span className="aal-stat-label">Accepted</span>
          <span className="aal-stat-number">{statusCounts.accepted}</span>
        </div>
        <div className="aal-stat-card aal-stat-rejected">
          <span className="aal-stat-label">Rejected</span>
          <span className="aal-stat-number">{statusCounts.rejected}</span>
        </div>
      </div>

      {/* Search & Filters Bar */}
      <div className="aal-search-filters">
        <div className="aal-search-wrapper">
          <span className="aal-search-icon">
            <Icons.Search />
          </span>
          <input
            type="text"
            className="aal-search-input"
            placeholder="Search by name, email, job title, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="aal-search-clear" onClick={clearSearch}>
              <Icons.Clear />
            </button>
          )}
        </div>

        <div className="aal-filter-controls">
          <div className="aal-filter-group">
            <select 
              className="aal-filter-select"
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
            >
              <option value="all">All Jobs</option>
              {uniqueJobs.map(job => (
                <option key={job} value={job}>{job}</option>
              ))}
            </select>
          </div>

          <div className="aal-filter-group">
            <select 
              className="aal-filter-select"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          {showJobFilter && (
            <button className="aal-clear-job-filter" onClick={clearJobFilter}>
              <Icons.Clear />
              Clear Job Filter
            </button>
          )}

          {(searchTerm || dateFilter !== 'all') && (
            <button className="aal-clear-filters" onClick={clearAllFilters}>
              <Icons.Clear />
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Results Count */}
      <div className="aal-results-count">
        <span>
          Showing <strong>{filteredApplications.length}</strong> of <strong>{applications.length}</strong> applications
          {searchTerm && <span> matching "<strong>{searchTerm}</strong>"</span>}
          {selectedJobId && applications.length > 0 && (
            <span className="aal-job-filter-info">
              <Icons.Filter />
              Filtered by: <strong>{applications[0]?.job_title}</strong>
            </span>
          )}
        </span>
        {filteredApplications.length > 0 && (
          <span className="aal-export-info">
            ({filteredApplications.length} {filteredApplications.length === 1 ? 'application' : 'applications'} ready for export)
          </span>
        )}
      </div>

      {/* Status Filters */}
      <div className="aal-filters">
        <button 
          className={`aal-filter-btn ${filter === 'all' ? 'aal-filter-active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
          <span className="aal-filter-count">{statusCounts.all}</span>
        </button>
        <button 
          className={`aal-filter-btn ${filter === 'pending' ? 'aal-filter-active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending
          <span className="aal-filter-count">{statusCounts.pending}</span>
        </button>
        <button 
          className={`aal-filter-btn ${filter === 'reviewed' ? 'aal-filter-active' : ''}`}
          onClick={() => setFilter('reviewed')}
        >
          Reviewed
          <span className="aal-filter-count">{statusCounts.reviewed}</span>
        </button>
        <button 
          className={`aal-filter-btn ${filter === 'shortlisted' ? 'aal-filter-active' : ''}`}
          onClick={() => setFilter('shortlisted')}
        >
          Shortlisted
          <span className="aal-filter-count">{statusCounts.shortlisted}</span>
        </button>
        <button 
          className={`aal-filter-btn ${filter === 'interviewed' ? 'aal-filter-active' : ''}`}
          onClick={() => setFilter('interviewed')}
        >
          Interviewed
          <span className="aal-filter-count">{statusCounts.interviewed}</span>
        </button>
        <button 
          className={`aal-filter-btn ${filter === 'accepted' ? 'aal-filter-active' : ''}`}
          onClick={() => setFilter('accepted')}
        >
          Accepted
          <span className="aal-filter-count">{statusCounts.accepted}</span>
        </button>
        <button 
          className={`aal-filter-btn ${filter === 'rejected' ? 'aal-filter-active' : ''}`}
          onClick={() => setFilter('rejected')}
        >
          Rejected
          <span className="aal-filter-count">{statusCounts.rejected}</span>
        </button>
      </div>

      {/* Table */}
      <div className="aal-table-wrapper">
        <table className="aal-table">
          <thead>
            <tr>
              <th className="aal-col-id">ID</th>
              <th className="aal-col-job">Job Title</th>
              <th className="aal-col-applicant">Applicant</th>
              <th className="aal-col-email">Email</th>
              <th className="aal-col-phone">Phone</th>
              <th className="aal-col-status">Status</th>
              <th className="aal-col-date">Applied Date</th>
              <th className="aal-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredApplications.length === 0 ? (
              <tr>
                <td colSpan={8} className="aal-empty-state">
                  <div className="aal-empty-icon">
                    <Icons.Empty />
                  </div>
                  <p className="aal-empty-text">No applications found</p>
                  <p className="aal-empty-subtext">
                    {searchTerm || jobFilter !== 'all' || dateFilter !== 'all' 
                      ? 'Try adjusting your search or filters' 
                      : selectedJobId 
                        ? 'No applications found for this job' 
                        : 'Applications will appear here once candidates start applying'}
                  </p>
                  {selectedJobId && (
                    <button className="aal-clear-job-filter-btn" onClick={clearJobFilter}>
                      <Icons.Clear />
                      View All Applications
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              filteredApplications.map((app) => (
                <tr key={app.id} className="aal-table-row">
                  <td className="aal-col-id">
                    <span className="aal-id-badge">#{app.id}</span>
                  </td>
                  <td className="aal-col-job">
                    <span className="aal-job-title">{app.job_title}</span>
                  </td>
                  <td className="aal-col-applicant">
                    <span className="aal-applicant-name">{app.full_name}</span>
                    {app.account_created && (
                      <span className="aal-account-badge" title="Account created automatically">
                        <Icons.NewAccount />
                      </span>
                    )}
                  </td>
                  <td className="aal-col-email">
                    <a href={`mailto:${app.email}`} className="aal-email-link">
                      <Icons.Email />
                      {app.email}
                    </a>
                  </td>
                  <td className="aal-col-phone">
                    <a href={`tel:${app.phone}`} className="aal-phone-link">
                      <Icons.Phone />
                      {app.phone}
                    </a>
                  </td>
                  <td className="aal-col-status">
                    <span 
                      className="aal-status-badge"
                      style={{ 
                        background: getStatusBackground(app.status),
                        color: getStatusColor(app.status)
                      }}
                    >
                      <span className="aal-status-dot" style={{ background: getStatusColor(app.status) }}></span>
                      {getStatusLabel(app.status)}
                    </span>
                  </td>
                  <td className="aal-col-date">
                    <span className="aal-date-text">
                      <Icons.Calendar />
                      {new Date(app.applied_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </td>
                  <td className="aal-col-actions">
                    <button 
                      className="aal-action-btn aal-action-view-details"
                      onClick={() => navigate(`/dashboard/admin/applications/${app.id}`)}
                    >
                      <Icons.View />
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminApplicationsList;